import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { CLASSES } from "@/lib/status";

export default function StudentDialog({ open, onOpenChange, student, onSaved }) {
  const [form, setForm] = useState({ name: "", class_level: "", school_name: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm(student ? { name: student.name, class_level: String(student.class_level), school_name: student.school_name || "" } : { name: "", class_level: "", school_name: "" });
  }, [open, student]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.class_level) return toast.error("Please select the class");
    setBusy(true);
    try {
      const payload = { ...form, class_level: Number(form.class_level) };
      if (student) await api.put(`/students/${student.id}`, payload);
      else await api.post("/students", payload);
      toast.success(student ? "Student updated" : "Student added");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white sm:max-w-md rounded-2xl" data-testid="student-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{student ? "Edit student" : "Add a student"}</DialogTitle>
          <DialogDescription>Choose the class your child is in right now. Questions are chosen for that exact class.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5 mt-2">
          <div className="space-y-2"><Label htmlFor="s-name">Student's name</Label><Input id="s-name" required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11" data-testid="student-name-input" /></div>
          <div className="space-y-2">
            <Label>Class</Label>
            <Select value={form.class_level} onValueChange={(v) => setForm({ ...form, class_level: v })}>
              <SelectTrigger className="h-11" data-testid="student-class-select"><SelectValue placeholder="Select class" /></SelectTrigger>
              <SelectContent className="bg-white">
                {CLASSES.map((c) => <SelectItem key={c} value={String(c)} data-testid={`student-class-option-${c}`}>Class {c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2"><Label htmlFor="s-school">School name (optional)</Label><Input id="s-school" value={form.school_name} onChange={(e) => setForm({ ...form, school_name: e.target.value })} className="h-11" data-testid="student-school-input" /></div>
          <button type="submit" disabled={busy} className="ds-btn w-full inline-flex items-center justify-center gap-2 bg-ds-plum text-white hover:bg-[#3d0232] disabled:opacity-60" data-testid="student-save-button">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {student ? "Save changes" : "Add student"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
